import moment from "moment";
import { PUBLISHER_APPROVAL_FIELDS } from "./constants";
var ISO_DATE_FORMAT = "YYYY-MM-DD";
var DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
/**
 * Brings a birth date to the ISO `YYYY-MM-DD` format.
 *
 * The form baseline (reset data) holds the birth date in the `DD/MM/YYYY`
 * display format, while the zod schema transforms the submitted value to ISO.
 * Comparing the raw strings made an untouched birth date look changed on every
 * save of a natural person (DB-552). Values in neither format (empty, partial
 * input) are returned unchanged.
 *
 * @param value a birth date in either format, or an empty value
 * @returns the ISO date, or the original value when it is not a full date
 */
var normalizeBirthDate = function (value) {
    if (typeof value !== "string")
        return value;
    var parsed = moment(value, [ISO_DATE_FORMAT, DISPLAY_DATE_FORMAT], true);
    return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : value;
};
/**
 * Per-field normalizers applied to BOTH the current and the baseline value
 * before they are compared, for fields whose representation differs between
 * the form baseline and the submitted data.
 */
var FIELD_NORMALIZERS = {
    birthDate: normalizeBirthDate,
};
var normalizeField = function (field, value) {
    return FIELD_NORMALIZERS[field] ? FIELD_NORMALIZERS[field](value) : value;
};
/**
 * Treats `null`, `undefined` and the empty string as one and the same "empty"
 * value.
 *
 * react-hook-form registers text inputs with a `""` value, while the data
 * fetched from the API (and therefore the form baseline) frequently uses `null`
 * for the same empty field. Without this normalization an untouched empty field
 * (e.g. DIČ/taxNumber) looks changed the moment RHF re-evaluates the whole form
 * — for instance after a field-array append — which is exactly the phantom
 * dirtiness that used to fire the approval dialog with no real change.
 *
 * @param value any form/baseline value
 * @returns `true` when the value carries no meaningful content
 */
var isEmptyValue = function (value) {
    return value === null || value === undefined || value === "";
};
/**
 * Recursively determines whether a value carries no meaningful content — it is
 * itself empty (see {@link isEmptyValue}), or it is an array/object whose every
 * entry is deeply empty. This lets an untouched `null` address compare equal to
 * an object whose fields are all `""` (e.g. `{ street: "", region: { id: "" } }`).
 *
 * @param value any form/baseline value
 * @returns `true` when the value contains nothing but empty leaves
 */
var isDeeplyEmpty = function (value) {
    if (isEmptyValue(value))
        return true;
    if (Array.isArray(value))
        return value.every(isDeeplyEmpty);
    if (typeof value === "object") {
        return Object.values(value).every(isDeeplyEmpty);
    }
    return false;
};
/**
 * Deep value equality with empty-variant normalization.
 *
 * Two values are considered equal when both are deeply empty (see
 * {@link isDeeplyEmpty}), when they are strictly equal, or when they are
 * arrays/objects whose entries are recursively equal.
 *
 * Object comparison projects the baseline onto the CURRENT (form) value's shape:
 * only the keys present on `a` (the current/form value) are compared, recursively.
 * The form never manages server-only fields (e.g. a `mainAddress.id`, or a
 * `region.code`) — they exist solely on the loaded baseline record. Comparing the
 * union of both key sets would therefore make an untouched address look changed
 * (baseline `id` vs. missing current `id`). Projecting onto the form shape ignores
 * those baseline-only keys, while a genuinely edited field still differs. Keys
 * present on the current value but missing on the baseline are compared against
 * `undefined`, so a newly filled field is still detected as a change.
 *
 * @param a first value (the current/form value at call sites)
 * @param b second value (the baseline value at call sites)
 * @returns `true` when the two values represent the same content
 */
var valuesEqual = function (a, b) {
    if (isDeeplyEmpty(a) && isDeeplyEmpty(b))
        return true;
    if (a === b)
        return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        return a.every(function (item, index) { return valuesEqual(item, b[index]); });
    }
    if (a !== null &&
        b !== null &&
        typeof a === "object" &&
        typeof b === "object" &&
        !Array.isArray(a) &&
        !Array.isArray(b)) {
        return Object.keys(a).every(function (key) {
            return valuesEqual(a[key], b[key]);
        });
    }
    return false;
};
/**
 * Returns `true` when at least one approval-required publisher field (see
 * {@link PUBLISHER_APPROVAL_FIELDS}) has actually changed compared to the form
 * baseline.
 *
 * The check compares field VALUES rather than react-hook-form's `dirtyFields`
 * leaf flags. `dirtyFields` cannot be trusted here: any field-array mutation
 * (adding a contact, phone, e-mail, alternative name, …) makes RHF re-register
 * every input and flip empty approved fields (notably taxNumber, whose default
 * is `null` but whose registered input value is `""`) to `dirty=true`, producing
 * a false positive. Comparing normalized values — treating `null`, `undefined`
 * and `""` as equal — is immune to that phantom dirtiness. Fields stored in a
 * different format on each side (birthDate) are normalized first.
 *
 * @param currentValues the current form values (e.g. `getValues()`)
 * @param baselineValues the baseline values the form was reset to
 *   (e.g. `formState.defaultValues`)
 * @param approvalFields fields requiring approval (defaults to the FSD list)
 * @returns `true` if any listed field genuinely differs from the baseline
 */
export var hasApprovalFieldChanged = function (currentValues, baselineValues, approvalFields) {
    if (approvalFields === void 0) { approvalFields = PUBLISHER_APPROVAL_FIELDS; }
    return approvalFields.some(function (field) {
        return !valuesEqual(normalizeField(field, currentValues === null || currentValues === void 0 ? void 0 : currentValues[field]), normalizeField(field, baselineValues === null || baselineValues === void 0 ? void 0 : baselineValues[field]));
    });
};
